
import { useState, useCallback, useEffect } from 'react';
import { EmailTemplate } from '@/types/emailTemplates';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { toast } from 'sonner';
import { setCachedTemplate } from './useTemplateCache';

export function useTemplateForm(templateId: string, template: EmailTemplate | null, isMountedRef: React.RefObject<boolean>) {
  const { updateTemplate } = useEmailTemplates();
  const [editedTemplate, setEditedTemplate] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [variablesJson, setVariablesJson] = useState('{}');
  const [variablesValid, setVariablesValid] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  
  // Initialize form state when template is loaded
  useEffect(() => {
    if (!template || !isMountedRef.current) return;
    
    setEditedTemplate({ ...template });
    setPreviewHtml(template.html_content || '');
    
    // Parse variables JSON safely
    try {
      const varsJson = template.variables 
        ? JSON.stringify(template.variables, null, 2)
        : '{}';
      setVariablesJson(varsJson);
      setVariablesValid(true);
    } catch (err) {
      console.error('Error parsing variables JSON:', err);
      setVariablesJson('{}');
      setVariablesValid(false);
    }
  }, [template, isMountedRef]);
  
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => {
    if (!editedTemplate) return;
    
    if (typeof e === 'string') {
      // Handle string input from editor
      setEditedTemplate(prev => prev ? ({
        ...prev,
        html_content: e
      }) : null);
      setPreviewHtml(e);
      return;
    }
    
    const { name, value } = e.target;
    setEditedTemplate(prev => prev ? ({
      ...prev,
      [name]: value
    }) : null);
    
    // Update preview in real-time when editing HTML content
    if (name === 'html_content') {
      setPreviewHtml(value);
    }
  }, [editedTemplate]);
  
  const handleSelectChange = useCallback((name: string, value: string) => {
    if (!editedTemplate) return;
    
    setEditedTemplate(prev => prev ? ({
      ...prev,
      [name]: value
    }) : null);
  }, [editedTemplate]);
  
  const handleVariablesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setVariablesJson(newValue);
    
    try {
      JSON.parse(newValue);
      setVariablesValid(true);
    } catch (err) {
      setVariablesValid(false);
    }
  }, []);
  
  const handleSave = useCallback(async () => {
    if (!editedTemplate || !templateId) return null;
    
    try {
      setLoadingAction(true);
      
      // Prepare the template data
      const updatedTemplate = {
        ...editedTemplate,
        variables: variablesValid ? JSON.parse(variablesJson) : editedTemplate.variables
      };
      
      // Save the template
      const result = await updateTemplate(templateId, updatedTemplate);
      
      // Update cache on successful save
      if (result) {
        setCachedTemplate(templateId, result);
      }
      
      setLoadingAction(false);
      return result;
    } catch (err: any) {
      setLoadingAction(false);
      console.error('Error saving template:', err);
      toast.error(`Error saving template: ${err.message}`);
      return null;
    }
  }, [editedTemplate, templateId, variablesJson, variablesValid, updateTemplate]);
  
  return {
    editedTemplate,
    previewHtml,
    variablesJson,
    variablesValid,
    loadingAction,
    handleInputChange,
    handleSelectChange,
    handleVariablesChange,
    handleSave
  };
}
